import React, { useState, useEffect, useCallback } from "react";
import { 
  HiOutlineClipboardDocumentList, 
  HiOutlinePlus, 
  HiOutlineDocumentArrowDown, 
  HiOutlineArrowUpTray, 
  HiOutlineXMark,
  HiOutlinePhoto,
  HiOutlineCheckCircle,
  HiOutlineWrenchScrewdriver,
  HiOutlineUserCircle,
  HiOutlineCog6Tooth,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineCalendarDays,
  HiOutlineArrowDownTray,
  HiOutlineDocumentText,
} from "react-icons/hi2";
import { authService, uploadService } from "../../../services";
import { toast } from "react-toastify";
import {
  showAppLoadingToast,
  appLoadingDone,
  appLoadingFailed,
} from "../../../utils/appLoadingToast";
import userService from "../../../services/userService";
import machineService from "../../../services/machineService";
import subassemblyService from "../../../services/subassemblyService";
import masterDataService from "../../../services/masterDataService";
import plantService from "../../../services/plantService";
import lineService from "../../../services/lineService";
import SharedModal from "../../../components/common/SharedModal";
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";
import "./PMStandardsPage.css";
import "../../HazardReport.css";

const FREQUENCY_OPTIONS = [
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Half Yearly", label: "Half Yearly" },
  { value: "Yearly", label: "Yearly" },
  { value: "No of Days", label: "No of Days" },
];

const EMPTY_FORM = {
  standardName: "",
  plantId: "",
  lineId: "",
  machineId: "",
  subassemblyId: "",
  frequency: "",
  dayOfWeek: "",
  dayOfMonth: "",
  quarterMonth: "",
  halfYearlyMonth: "",
  yearlyMonth: "",
  yearlyDay: "",
  noOfDays: "",
  assignedUserId: "",
  why: "",
  how: "",
  evidenceUrl: "",
  finding: "",
  actionTaken: "",
  isActive: true,
  startDate: "",
  minutes: "",
  isMachineShutdownRequired: false,
};

const PMStandardsPage = () => {
  const tenantId = authService.getTenantId();

  const [standards, setStandards] = useState([]);
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [machines, setMachines] = useState([]);
  const [subassemblies, setSubassemblies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allMachines, setAllMachines] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [plantFilter, setPlantFilter] = useState("");
  const [lineFilter, setLineFilter] = useState("");
  const [frequencyFilter, setFrequencyFilter] = useState("");
  const [shutdownFilter, setShutdownFilter] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const fileInputRef = React.useRef(null);

  const loadStandards = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) setLoading(true);
        setError("");
        const data = await masterDataService.getPMStandards(tenantId);
        setStandards(data || []);
      } catch {
        setError("Failed to load PM Standards. Please try again.");
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [tenantId],
  );

  const loadDropdownData = useCallback(async () => {
    try {
      const [plantsData, machinesData, empData] = await Promise.all([
        plantService.getAllPlants(),
        machineService.getAllMachines(),
        userService.getAllUsers(),
      ]);
      setPlants(plantsData || []);
      setAllMachines(machinesData || []);
      setEmployees(empData || []);

      // Fetch all lines for filters
      const allLinesData = await lineService.getAllLines();
      setAllLines(allLinesData || []);

      // Auto-select plant ONLY for single-plant users
      if (plantsData?.length === 1) {
        setPlantFilter(plantsData[0].plantId.toString());
      }
    } catch (err) {
      console.error("Failed to load dropdown data", err);
    }
  }, []);

  useEffect(() => {
    if (tenantId) {
      loadStandards();
      loadDropdownData();
    }
  }, [tenantId, loadStandards, loadDropdownData]);

  useEffect(() => {
    if (formData.plantId) {
      lineService
        .getAllLines(formData.plantId)
        .then((r) => setLines(r || []))
        .catch(() => setLines([]));
    } else {
      setLines([]);
    }
  }, [formData.plantId]);

  useEffect(() => {
    if (formData.lineId) {
      machineService
        .getAllMachines(formData.lineId)
        .then((r) => setMachines(r || []))
        .catch(() => setMachines([]));
    } else {
      setMachines([]);
    }
  }, [formData.lineId]);

  useEffect(() => {
    if (formData.machineId) {
      subassemblyService
        .getAllSubassemblies(formData.machineId)
        .then((r) => setSubassemblies(r || []))
        .catch(() => setSubassemblies([]));
    } else {
      setSubassemblies([]);
    }
  }, [formData.machineId]);

  const openAddDrawer = () => {
    setEditingItem(null);

    // Auto-select plant for new records ONLY for single-plant users
    let initialPlantId = "";
    if (plants?.length === 1) {
      initialPlantId = plants[0].plantId.toString();
    }

    setFormData({ ...EMPTY_FORM, plantId: initialPlantId });
    setFormErrors({});
    setEvidenceFile(null);
    setSubassemblies([]);
    setDrawerOpen(true);
  };

  const openEditDrawer = (item) => {
    setEditingItem(item);
    setFormData({
      standardName: item.standardName || "",
      plantId: item.plantId?.toString() || "",
      lineId: item.lineId?.toString() || "",
      machineId: item.machineId?.toString() || "",
      subassemblyId: item.subassemblyId?.toString() || "",
      frequency: item.frequency || "",
      dayOfWeek: item.dayOfWeek || "",
      dayOfMonth: item.dayOfMonth?.toString() || "",
      quarterMonth: item.quarterMonth || "",
      halfYearlyMonth: item.halfYearlyMonth || "",
      yearlyMonth: item.yearlyMonth || "",
      yearlyDay: item.yearlyDay?.toString() || "",
      startDate: item.startDate
        ? new Date(item.startDate).toISOString().split("T")[0]
        : "",
      noOfDays: item.noOfDays?.toString() || "",
      assignedUserId: item.assignedUserId?.toString() || "",
      why: item.why || "",
      how: item.how || "",
      evidenceUrl: item.evidenceUrl || "",
      finding: item.finding || "",
      actionTaken: item.actionTaken || "",
      isActive: item.isActive !== undefined ? item.isActive : true,
      minutes: item.minutes?.toString() || "",
      isMachineShutdownRequired: item.isMachineShutdownRequired || false,
    });
    setFormErrors({});
    setEvidenceFile(null);
    setDrawerOpen(true);
  };

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => {
      setEditingItem(null);
      setFormData({ ...EMPTY_FORM });
      setFormErrors({});
      setEvidenceFile(null);
    }, 400);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen, closeDrawer]);

  const handleField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.standardName.trim())
      errors.standardName = "Standard Name is required";
    if (!formData.plantId) errors.plantId = "Plant is required";
    if (!formData.lineId) errors.lineId = "Line is required";
    if (!formData.machineId) errors.machineId = "Machine is required";
    if (!formData.frequency) errors.frequency = "Frequency is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const toastId = toast.loading(editingItem ? "Updating PM Standard..." : "Creating PM Standard...");
    try {
      setSubmitting(true);
      setError("");
      let evidenceUrl = formData.evidenceUrl;
      if (evidenceFile) {
        const { url } = await uploadService.uploadFile(evidenceFile);
        evidenceUrl = url;
      }
      const payload = {
        tenantId,
        standardName: formData.standardName.trim(),
        plantId: formData.plantId ? parseInt(formData.plantId) : null,
        lineId: formData.lineId ? parseInt(formData.lineId) : null,
        machineId: formData.machineId ? parseInt(formData.machineId) : null,
        subassemblyId: formData.subassemblyId
          ? parseInt(formData.subassemblyId)
          : null,
        startDate: formData.startDate || null,
        frequency: formData.frequency || null,
        dayOfWeek:
          formData.frequency === "Weekly" ? formData.dayOfWeek || null : null,
        dayOfMonth:
          formData.frequency === "Monthly"
            ? parseInt(formData.dayOfMonth) || null
            : null,
        quarterMonth:
          formData.frequency === "Quarterly"
            ? formData.quarterMonth || null
            : null,
        halfYearlyMonth:
          formData.frequency === "Half Yearly"
            ? formData.halfYearlyMonth || null
            : null,
        yearlyMonth:
          formData.frequency === "Yearly" ? formData.yearlyMonth || null : null,
        yearlyDay:
          formData.frequency === "Yearly"
            ? parseInt(formData.yearlyDay) || null
            : null,
        noOfDays:
          formData.frequency === "No of Days"
            ? parseInt(formData.noOfDays) || null
            : null,
        assignedUserId: formData.assignedUserId
          ? parseInt(formData.assignedUserId)
          : null,
        why: formData.why.trim() || null,
        how: formData.how.trim() || null,
        evidenceUrl: evidenceUrl || null,
        finding: formData.finding.trim() || null,
        actionTaken: formData.actionTaken.trim() || null,

        isActive: formData.isActive,
        minutes: formData.minutes ? parseInt(formData.minutes) : null,
        isMachineShutdownRequired: formData.isMachineShutdownRequired,
      };
      if (editingItem) {
        payload.standardId = editingItem.standardId;
        await masterDataService.updatePMStandard(
          editingItem.standardId,
          payload,
        );
      } else {
        await masterDataService.createPMStandard(payload);
      }
      closeDrawer();
      loadStandards(false);
      toast.update(toastId, { render: editingItem ? "PM Standard updated successfully!" : "PM Standard created successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to save PM Standard.";
      setError(msg);
      toast.update(toastId, { render: msg, type: "error", isLoading: false, autoClose: 3000, closeButton: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const toastId = toast.loading("Deleting PM Standard...");
    try {
      setError("");
      await masterDataService.deletePMStandard(id);
      await loadStandards(false);
      setDeleteConfirm(null);
      toast.update(toastId, { render: "PM Standard deleted successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch {
      const msg = "Failed to delete PM Standard.";
      setError(msg);
      toast.update(toastId, { render: msg, type: "error", isLoading: false, autoClose: 3000, closeButton: true });
    }
  };

  const filtered = standards.filter((s) => {
    const matchesSearch =
      s.standardCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.standardName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.machineName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPlant = !plantFilter || s.plantId?.toString() === plantFilter;
    const matchesLine = !lineFilter || s.lineId?.toString() === lineFilter;

    const matchesFrequency =
      !frequencyFilter || s.frequency === frequencyFilter;

    const matchesShutdown =
      !shutdownFilter ||
      (shutdownFilter === "Yes" && s.isMachineShutdownRequired) ||
      (shutdownFilter === "No" && !s.isMachineShutdownRequired);

    return (
      matchesSearch &&
      matchesPlant &&
      matchesLine &&
      matchesFrequency &&
      matchesShutdown
    );
  });

  const handleDownloadTemplate = async () => {
    showAppLoadingToast("Preparing template…");
    try {
      setSubmitting(true);
      const data = await masterDataService.generateTemplate("PMStandards");
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "PMStandards_Template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      appLoadingDone("Template downloaded successfully!");
    } catch (err) {
      appLoadingFailed("Failed to download template.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    showAppLoadingToast("Importing PM Standards…");
    try {
      setSubmitting(true);
      setError("");
      setImportErrors([]);
      const result = await masterDataService.importExcel("PMStandards", file);
      appLoadingDone(
        `Import successful (${result.successCount} succeeded, ${result.errorCount} failed).`,
      );
      if (result.errorCount > 0) {
        setError(result.message);
        setImportErrors(result.errors || []);
      }
      await loadStandards();
    } catch (err) {
      const result = err.response?.data;
      const msg = result?.message || err.message || "Failed to import file.";
      setError(msg);
      if (result?.errors) {
        setImportErrors(result.errors);
      }
      appLoadingFailed(msg);
    } finally {
      setSubmitting(false);
      e.target.value = null;
    }
  };

  const getMachineName = (id) => {
    const m = machines.find((x) => x.machineId?.toString() === id?.toString());
    return m ? `${m.machineCode} - ${m.machineName}` : id || "-";
  };

  const getEmployeeName = (id) => {
    const u = employees.find((x) => x.userId?.toString() === id?.toString());
    return u ? u.fullName || u.userName : "-";
  };

  if (loading) {
    return (
      <div className="pagecontainer pm-masters-page">
        <PageLoading message="Loading PM standards…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer">

      {/* ===== PAGE HEADER — matches rest of PM module ===== */}
      <div className="page-header">
        <div className="header-content">
          <h1>PM Standards</h1>
          <p>
            Manage PM standards for machines &amp; subassemblies for Planned
            Maintenance
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleDownloadTemplate}
          >
            <HiOutlineDocumentArrowDown />
            Download Template
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fileInputRef.current.click()}
          >
            <HiOutlineArrowUpTray />
            Import Excel
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportExcel}
            style={{ display: "none" }}
            accept=".xlsx,.xls"
          />
          <button className="btn btn-primary" onClick={openAddDrawer}>
            <HiOutlinePlus />
            Add PM Standard
          </button>
        </div>
      </div>      {error && <div className="error-message">{error}</div>}
      
      {importErrors.length > 0 && (
        <div className="import-errors-container" style={{ margin: "10px 0", padding: "15px", border: "1px solid #ffcccb", borderRadius: "8px", backgroundColor: "#fff5f5" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <h3 style={{ margin: 0, color: "#d32f2f", fontSize: "16px" }}>Import Details / Errors:</h3>
            <button 
              className="btn-icon-delete" 
              onClick={() => setImportErrors([])}
              title="Clear Errors"
              style={{ padding: "4px" }}
            >
              <HiOutlineXMark />
            </button>
          </div>
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead style={{ backgroundColor: "#fee2e2", textAlign: "left" }}>
                <tr>
                  <th style={{ padding: "8px", border: "1px solid #fecaca" }}>Row</th>
                  <th style={{ padding: "8px", border: "1px solid #fecaca" }}>Field</th>
                  <th style={{ padding: "8px", border: "1px solid #fecaca" }}>Error Message</th>
                </tr>
              </thead>
              <tbody>
                {importErrors.map((err, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: "8px", border: "1px solid #fecaca" }}>{err.rowNumber || idx + 1}</td>
                    <td style={{ padding: "8px", border: "1px solid #fecaca" }}>{err.field || "-"}</td>
                    <td style={{ padding: "8px", border: "1px solid #fecaca", color: "#d32f2f" }}>{err.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== TABLE CONTAINER ===== */}
      <div className="table-container pad10 kizantable">
        <div className="toolbar-section" style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          gap: "16px", 
          padding: "12px 16px",
          marginBottom: "12px",
          border: "1px solid #eef2f6",
          borderRadius: "12px",
          background: "#fff",
          flexWrap: "wrap"
        }}>
          <div className="search-box" style={{ flex: "0 1 240px", position: "relative" }}>
            <input
              type="text"
              placeholder="Search standards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{ 
                height: "40px", 
                borderRadius: "10px", 
                border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                padding: "0 16px",
                fontSize: "14px"
              }}
            />
          </div>
          
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            <div className="filter-box">
              <select
                value={plantFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setPlantFilter(value);
                  setLineFilter("");
                }}
                className="filter-select"
                style={{ minWidth: "135px", height: "40px", borderRadius: "8px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", backgroundColor: "#f8fafc" }}
              >
                <option value="">All Plants</option>
                {plants.map((p) => (
                  <option key={p.plantId} value={p.plantId}>
                    {p.plantName}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-box">
              <select
                value={lineFilter}
                onChange={(e) => setLineFilter(e.target.value)}
                className="filter-select"
                style={{ minWidth: "135px", height: "40px", borderRadius: "8px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", backgroundColor: "#f8fafc" }}
              >
                <option value="">All Lines</option>
                {allLines
                  .filter(
                    (l) => !plantFilter || l.plantId?.toString() === plantFilter,
                  )
                  .map((l) => (
                    <option key={l.lineId} value={l.lineId}>
                      {l.lineName}
                    </option>
                  ))}
              </select>
            </div>

            <div className="filter-box">
              <select
                value={frequencyFilter}
                onChange={(e) => setFrequencyFilter(e.target.value)}
                className="filter-select"
                style={{ minWidth: "145px", height: "40px", borderRadius: "8px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", backgroundColor: "#f8fafc" }}
              >
                <option value="">All Frequencies</option>
                {FREQUENCY_OPTIONS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-box">
              <select
                value={shutdownFilter}
                onChange={(e) => setShutdownFilter(e.target.value)}
                className="filter-select"
                style={{ minWidth: "165px", height: "40px", borderRadius: "8px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", backgroundColor: "#f8fafc" }}
              >
                <option value="">Machine Shutdown</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="table-info" style={{ 
              color: "#334155", 
              padding: "0 8px", 
              fontWeight: "600",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              height: "40px"
            }}>
              {filtered.length}{" "}
              {filtered.length === 1 ? "standard" : "standards"}
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClipboardDocumentList className="empty-icon" />
            <h3>No PM Standards found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first PM Standard"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={openAddDrawer}>
                Add PM Standard
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead style={{ backgroundColor: "#dbe2df" }}>
              <tr>
                <th style={{ backgroundColor: "transparent" }}>ID</th>
                <th style={{ backgroundColor: "transparent" }}>Code</th>
                <th style={{ backgroundColor: "transparent" }}>Name</th>
                <th style={{ backgroundColor: "transparent" }}>Machine</th>
                <th style={{ backgroundColor: "transparent" }}>Subassembly</th>
                <th style={{ backgroundColor: "transparent" }}>Frequency</th>
                <th style={{ backgroundColor: "transparent" }}>Min</th>
                <th style={{ backgroundColor: "transparent" }}>Shutdown</th>
                <th style={{ backgroundColor: "transparent" }}>Employee</th>
                <th style={{ backgroundColor: "transparent" }}>Active</th>
                <th style={{ backgroundColor: "transparent" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, index) => (
                <tr key={item.standardId || item.id}>
                  <td>{index + 1}</td>
                  <td className="name-cell">{item.standardCode}</td>
                  <td>{item.standardName}</td>
                  <td>{item.machineName || getMachineName(item.machineId)}</td>
                  <td>{item.subassemblyName || "-"}</td>
                  <td>{item.frequency || "-"}</td>
                  <td>{item.minutes ? `${item.minutes}m` : "-"}</td>
                  <td>{item.isMachineShutdownRequired ? "Yes" : "No"}</td>
                  <td>
                    {item.assignedUserName ||
                      getEmployeeName(item.assignedUserId)}
                  </td>

                  <td>{item.isActive ? "Yes" : "No"}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-view"
                        title="Add Findings"
                        onClick={() => openEditDrawer(item)}
                      >
                        <HiOutlineClipboardDocumentList />
                      </button>
                      <button
                        className="btn-icon-edit"
                        title="Edit"
                        onClick={() => openEditDrawer(item)}
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        title="Delete"
                        onClick={() => setDeleteConfirm(item)}
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <>
        <div
          className={`pms-drawer-backdrop ${drawerOpen ? "open" : ""}`}
          onClick={() => {
            if (!submitting) closeDrawer();
          }}
          aria-hidden={!drawerOpen}
        />
        <div
          className={`pms-drawer ${drawerOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pms-drawer-title"
        >
          <div className="pms-drawer-header">
            <h3 id="pms-drawer-title">
              {editingItem
                ? editingItem.standardCode || "Edit PM Standard"
                : "Add New PM Standard"}
            </h3>
            <div className="pms-drawer-header-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDrawer}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="pm-standard-form"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting
                  ? editingItem
                    ? "Updating..."
                    : "Creating..."
                  : editingItem
                    ? "Update Standard"
                    : "Save Standard"}
              </button>
              <button
                type="button"
                className="drawer-close-btn pms-drawer-close"
                onClick={closeDrawer}
                disabled={submitting}
                aria-label="Close"
              >
                <HiOutlineXMark size={22} />
              </button>
            </div>
          </div>
          <div className="pms-drawer-body">
            <form id="pm-standard-form" onSubmit={handleSubmit} noValidate>
            {/* SECTION 1 — WHAT */}
            <div className="form-section">
              <h3
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <HiOutlineClipboardDocumentList
                  size={20}
                  style={{ color: "#6366f1" }}
                />
                WHAT — Standard Details
              </h3>

              {editingItem && (
                <div className="form-row">
                  <div className="form-col">
                    <label>Standard Code</label>
                    <input
                      type="text"
                      disabled
                      value={editingItem.standardCode}
                      style={{
                        backgroundColor: "#f8fafc",
                        cursor: "not-allowed",
                        color: "#94a3b8",
                        borderColor: "#e2e8f0",
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="form-row full-width">
                <div className="form-col">
                  <label className="required-label">Standard Name</label>
                  <textarea
                    className={formErrors.standardName ? "error" : ""}
                    placeholder="Enter standard name or description…"
                    rows={3}
                    value={formData.standardName}
                    onChange={(e) =>
                      handleField("standardName", e.target.value)
                    }
                    style={{
                      minHeight: "80px",
                      resize: "vertical",
                      width: "100%",
                      border: formErrors.standardName
                        ? "1px solid #ef4444"
                        : "1px solid #cbd5e1",
                      borderRadius: "12px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      color: "#0f172a",
                      outline: "none",
                    }}
                  />
                  {formErrors.standardName && (
                    <span className="field-error">
                      {formErrors.standardName}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 2 — WHERE */}
            <div className="form-section">
              <h3
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <HiOutlineWrenchScrewdriver
                  size={20}
                  style={{ color: "#6366f1" }}
                />
                WHERE — Asset Location
              </h3>
              <div className="form-row two-col">
                <div className="form-col">
                  <label className="required-label">Plant</label>
                  <select
                    value={formData.plantId}
                    onChange={(e) => {
                      handleField("plantId", e.target.value);
                      handleField("lineId", "");
                      handleField("machineId", "");
                      handleField("subassemblyId", "");
                    }}
                    style={{
                      border: formErrors.plantId
                        ? "1px solid #ef4444"
                        : "1px solid #e2e8f0; border-color: var(--border-primary)",
                    }}
                  >
                    <option value="">Please Select Plant</option>
                    {plants.map((p) => (
                      <option key={p.plantId} value={p.plantId}>
                        {p.plantName}
                      </option>
                    ))}
                  </select>
                  {formErrors.plantId && (
                    <span className="field-error">{formErrors.plantId}</span>
                  )}
                </div>
                <div className="form-col">
                  <label className="required-label">Line</label>
                  <select
                    value={formData.lineId}
                    onChange={(e) => {
                      handleField("lineId", e.target.value);
                      handleField("machineId", "");
                      handleField("subassemblyId", "");
                    }}
                    disabled={!formData.plantId}
                    style={{
                      border: formErrors.lineId
                        ? "1px solid #ef4444"
                        : "1px solid #e2e8f0; border-color: var(--border-primary)",
                    }}
                  >
                    <option value="">Please Select Line</option>
                    {lines.map((l) => (
                      <option key={l.lineId} value={l.lineId}>
                        {l.lineName}
                      </option>
                    ))}
                  </select>
                  {formErrors.lineId && (
                    <span className="field-error">{formErrors.lineId}</span>
                  )}
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-col">
                  <label className="required-label">Machine</label>
                  <select
                    value={formData.machineId}
                    onChange={(e) => {
                      handleField("machineId", e.target.value);
                      handleField("subassemblyId", "");
                    }}
                    disabled={!formData.lineId}
                    style={{
                      border: formErrors.machineId
                        ? "1px solid #ef4444"
                        : "1px solid #e2e8f0; border-color: var(--border-primary)",
                    }}
                  >
                    <option value="">Please Select Machine</option>
                    {machines.map((m) => (
                      <option key={m.machineId} value={m.machineId}>
                        {m.machineCode} - {m.machineName}
                      </option>
                    ))}
                  </select>
                  {formErrors.machineId && (
                    <span className="field-error">{formErrors.machineId}</span>
                  )}
                </div>
                <div className="form-col">
                  <label>Subassembly</label>
                  <select
                    value={formData.subassemblyId}
                    onChange={(e) =>
                      handleField("subassemblyId", e.target.value)
                    }
                    disabled={!formData.machineId}
                  >
                    <option value="">Select Subassembly (Optional)</option>
                    {subassemblies.map((s) => (
                      <option key={s.subassemblyId} value={s.subassemblyId}>
                        {s.subassemblyCode} - {s.subassemblyName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* SECTION 3 — WHEN & WHO */}
            <div className="form-section">
              <h3
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <HiOutlineCalendarDays size={20} style={{ color: "#6366f1" }} />
                WHEN &amp; WHO — Schedule &amp; Responsibility
              </h3>

              <div className="form-row two-col">
                <div className="form-col">
                  <label className="required-label">Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleField("startDate", e.target.value)}
                    style={{
                      border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                      borderRadius: "12px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      color: "#0f172a",
                      outline: "none",
                      width: "100%",
                    }}
                  />
                </div>

                <div className="form-col">
                  <label className="required-label">Frequency</label>
                  <select
                    className={formErrors.frequency ? "error" : ""}
                    value={formData.frequency}
                    onChange={(e) => {
                      handleField("frequency", e.target.value);
                      handleField("dayOfWeek", "");
                      handleField("dayOfMonth", "");
                      handleField("quarterMonth", "");
                      handleField("halfYearlyMonth", "");
                      handleField("yearlyMonth", "");
                      handleField("yearlyDay", "");
                      handleField("noOfDays", "");
                    }}
                    style={{
                      border: formErrors.frequency
                        ? "1px solid #ef4444"
                        : "1px solid #e2e8f0; border-color: var(--border-primary)",
                    }}
                  >
                    <option value="">Select Frequency</option>
                    {FREQUENCY_OPTIONS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  {formErrors.frequency && (
                    <span className="field-error">{formErrors.frequency}</span>
                  )}
                </div>

                <div className="form-col" style={{ minHeight: "72px" }}>
                  {formData.frequency === "Weekly" && (
                    <>
                      <label>Day of Week</label>
                      <select
                        value={formData.dayOfWeek}
                        onChange={(e) =>
                          handleField("dayOfWeek", e.target.value)
                        }
                      >
                        <option value="">Select Day</option>
                        {[
                          "Sunday",
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                          "Saturday",
                        ].map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  {formData.frequency === "Monthly" && (
                    <>
                      <label>Day of Month</label>
                      <select
                        value={formData.dayOfMonth}
                        onChange={(e) =>
                          handleField("dayOfMonth", e.target.value)
                        }
                      >
                        <option value="">Select Day (1–31)</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(
                          (d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ),
                        )}
                      </select>
                    </>
                  )}
                  {formData.frequency === "Quarterly" && (
                    <>
                      <label>Starting Month of Quarter</label>
                      <select
                        value={formData.quarterMonth}
                        onChange={(e) =>
                          handleField("quarterMonth", e.target.value)
                        }
                      >
                        <option value="">Select Month</option>
                        {[
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ].map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  {formData.frequency === "Half Yearly" && (
                    <>
                      <label>Starting Month</label>
                      <select
                        value={formData.halfYearlyMonth}
                        onChange={(e) =>
                          handleField("halfYearlyMonth", e.target.value)
                        }
                      >
                        <option value="">Select Month</option>
                        {[
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ].map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  {formData.frequency === "Yearly" && (
                    <>
                      <label>Month &amp; Day</label>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <select
                          value={formData.yearlyMonth}
                          onChange={(e) =>
                            handleField("yearlyMonth", e.target.value)
                          }
                          style={{ flex: 1 }}
                        >
                          <option value="">Month</option>
                          {[
                            "January",
                            "February",
                            "March",
                            "April",
                            "May",
                            "June",
                            "July",
                            "August",
                            "September",
                            "October",
                            "November",
                            "December",
                          ].map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <select
                          value={formData.yearlyDay}
                          onChange={(e) =>
                            handleField("yearlyDay", e.target.value)
                          }
                          style={{ flex: 1 }}
                        >
                          <option value="">Day</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ),
                          )}
                        </select>
                      </div>
                    </>
                  )}
                  {formData.frequency === "No of Days" && (
                    <>
                      <label>Number of Days</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="e.g. 7"
                        value={formData.noOfDays}
                        onChange={(e) =>
                          handleField("noOfDays", e.target.value)
                        }
                      />
                    </>
                  )}
                  {formData.frequency === "Daily" && (
                    <>
                      <label style={{ visibility: "hidden" }}>
                        No Selection
                      </label>
                      <div
                        style={{
                          height: "48px",
                          backgroundColor: "#f8fafc",
                          border: "1px dashed #cbd5e1",
                          borderRadius: "14px",
                          display: "flex",
                          alignItems: "center",
                          padding: "0 18px",
                          color: "#64748b",
                          fontSize: "13px",
                          fontWeight: "600",
                          width: "100%",
                        }}
                      >
                        Runs every day
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* SECTION 3B — EXECUTION DETAILS (Added Who, Minutes, Shutdown in one row) */}
              <div
                className="form-row"
                style={{ alignItems: "flex-end", gap: "24px" }}
              >
                <div className="form-col">
                  <label>Who — Select Employee</label>
                  <select
                    value={formData.assignedUserId}
                    onChange={(e) =>
                      handleField("assignedUserId", e.target.value)
                    }
                  >
                    <option value="">Select Employee</option>
                    {employees.map((u) => (
                      <option key={u.userId} value={u.userId}>
                        {u.fullName || u.userName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-col">
                  <label>Duration (Minutes)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 30"
                    value={formData.minutes}
                    onChange={(e) => handleField("minutes", e.target.value)}
                    style={{
                      border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                      borderRadius: "14px",
                      padding: "0 18px",
                      height: "48px",
                      fontSize: "14px",
                      color: "#0f172a",
                      outline: "none",
                      width: "100%",
                    }}
                  />
                </div>

                <div className="form-col">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      height: "48px",
                      marginBottom: "0",
                    }}
                  >
                    <label
                      className="switch-styled"
                      style={{ marginBottom: 0 }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.isMachineShutdownRequired}
                        onChange={(e) =>
                          handleField(
                            "isMachineShutdownRequired",
                            e.target.checked,
                          )
                        }
                      />
                      <span className="slider-styled"></span>
                    </label>
                    <label
                      className="toggle-label-text"
                      style={{
                        marginBottom: 0,
                        fontWeight: "600",
                        color: "#334155",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Shutdown Required?
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 4 — WHY & HOW */}
            <div className="form-section">
              <h3
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <HiOutlineCog6Tooth size={20} style={{ color: "#6366f1" }} />
                WHY &amp; HOW — Justification &amp; Method
              </h3>

              <div className="form-row full-width">
                <div className="form-col">
                  <label>Why (Reason / Purpose)</label>
                  <textarea
                    placeholder="Enter why this standard is needed…"
                    value={formData.why}
                    onChange={(e) => handleField("why", e.target.value)}
                    style={{
                      minHeight: "80px",
                      resize: "vertical",
                      width: "100%",
                      border: "1px solid #cbd5e1",
                      borderRadius: "12px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      color: "#0f172a",
                      outline: "none",
                    }}
                  />
                </div>
              </div>
              <div className="form-row full-width">
                <div className="form-col">
                  <label>How (Method / Procedure)</label>
                  <textarea
                    placeholder="Describe how to perform this standard…"
                    value={formData.how}
                    onChange={(e) => handleField("how", e.target.value)}
                    style={{
                      minHeight: "80px",
                      resize: "vertical",
                      width: "100%",
                      border: "1px solid #cbd5e1",
                      borderRadius: "12px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      color: "#0f172a",
                      outline: "none",
                    }}
                  />
                </div>
              </div>

              <div className="form-row full-width">
                <div className="form-col" style={{ flex: "100%" }}>
                  <label>Reference Image/Video (Instructional)</label>
                  <div
                    style={{
                      padding: "20px",
                      border: "2px dashed #e2e8f0; border-color: var(--border-primary)",
                      borderRadius: "16px",
                      backgroundColor: "#fcfdfe",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      marginBottom: "16px",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.borderColor = "#6366f1")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.borderColor = "#e2e8f0")
                    }
                  >
                    <input
                      type="file"
                      id="evidenceUpload"
                      accept="image/*,video/*"
                      onChange={(e) => {
                        setEvidenceFile(e.target.files[0] || null);
                      }}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor="evidenceUpload"
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "8px",
                        color: "#64748b",
                      }}
                    >
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "14px",
                          backgroundColor: "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#6366f1",
                          marginBottom: "4px",
                        }}
                      >
                        <HiOutlinePhoto size={24} />
                      </div>
                      <span
                        style={{
                          fontWeight: "700",
                          color: "#1e293b",
                          fontSize: "14px",
                        }}
                      >
                        Click to upload reference
                      </span>
                      <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                        Images or videos up to 10MB
                      </span>
                    </label>
                  </div>

                  {(formData.evidenceUrl || evidenceFile) && (
                    <div
                      style={{
                        fontSize: "0.85rem",
                        color: "#6366f1",
                        fontWeight: "700",
                        marginTop: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {evidenceFile ? (
                        <>
                          <HiOutlineCheckCircle />
                          <span>Selected: {evidenceFile.name}</span>
                        </>
                      ) : (
                        <a
                          href={formData.evidenceUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: "#6366f1",
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <HiOutlinePhoto />
                          View existing file ↗
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 5 — FINDINGS & STATUS */}
            <div className="form-section">
              <h3
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <HiOutlineUserCircle size={20} style={{ color: "#6366f1" }} />
                {editingItem ? "FINDINGS & STATUS" : "STATUS"}
              </h3>

              {editingItem && (
                <div className="form-row two-col">
                  <div className="form-col">
                    <label>Finding</label>
                    <input
                      type="text"
                      placeholder="Enter finding"
                      value={formData.finding}
                      onChange={(e) => handleField("finding", e.target.value)}
                    />
                  </div>
                  <div className="form-col">
                    <label>Action Taken</label>
                    <input
                      type="text"
                      placeholder="Enter action taken"
                      value={formData.actionTaken}
                      onChange={(e) =>
                        handleField("actionTaken", e.target.value)
                      }
                    />
                  </div>
                </div>
              )}

              <div className="form-row two-col">
                <div
                  className="form-col"
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <label className="switch-styled" style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        handleField("isActive", e.target.checked)
                      }
                    />
                    <span className="slider-styled"></span>
                  </label>
                  <label
                    className="toggle-label-text"
                    style={{
                      marginBottom: 0,
                      fontWeight: "600",
                      color: "#334155",
                      fontSize: "14px",
                    }}
                  >
                    Active Status
                  </label>
                </div>
              </div>
            </div>
          </form>
          </div>
        </div>
      </>

      {/* ===== DELETE CONFIRMATION MODAL ===== */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="delete-modal"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm.standardId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.standardName}</strong>?
          </p>
          <p className="warning-text">This action cannot be undone.</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default PMStandardsPage;
